/**
 * PasswordReveal — 他人のパスワードを確認する欄（ユーザー管理の編集画面・人材カード共用）
 *
 * - 扱えるのは 陸上管理者・システム管理者・運営管理者だけ（canManagePasswordOf）。
 *   システム管理者のパスワードは、システム管理者か運営管理者だけが扱える。
 * - 最初は伏せ字。「表示」を押したときだけサーバーから取り出し、60秒で自動的に伏せる。
 *   表示した記録は操作履歴に残る。
 * - 本人が自分で変更したパスワードは保管していないため表示できない（理由を案内する）。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound } from 'lucide-react';
import * as api from '../services/api';

/** ログイン中の管理者（useAuth が localStorage に保存している） */
function currentAdmin(): { user_role?: string; is_super_admin?: boolean } {
  try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; }
}

/** 対象ユーザー（ロール）のパスワードを、いまの管理者が閲覧・変更できるか */
export function canManagePasswordOf(targetRole: string | undefined | null): boolean {
  const me = currentAdmin();
  if (me.is_super_admin) return true;
  if (!['system_admin', 'shore_admin'].includes(me.user_role ?? '')) return false;
  if (targetRole === 'system_admin' && me.user_role !== 'system_admin') return false;
  return true;
}

/** いまの管理者が、そもそも他人のパスワードを扱える立場か（ボタンの出し分け用） */
export function canManagePasswords(): boolean {
  const me = currentAdmin();
  return !!me.is_super_admin || ['system_admin', 'shore_admin'].includes(me.user_role ?? '');
}

const AUTO_HIDE_MS = 60_000;

function fmtChanged(at?: string | null, by?: string | null): string | null {
  if (!at) return null;
  const d = new Date(at.includes('T') ? at : at.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return null;
  return `${d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} に ${by ?? '不明'} が設定`;
}

export default function PasswordReveal({ userId, targetRole, employeeCode, hint, refreshKey }: {
  userId: string;
  targetRole?: string | null;
  /** ログインID（社員コード）を一緒に出す場合 */
  employeeCode?: string | null;
  /** 表示できないときの案内（画面ごとに「編集から設定」などを出し分ける） */
  hint?: string;
  /** 値が変わったときに伏せ字へ戻すためのキー（パスワード変更後など） */
  refreshKey?: unknown;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [changed, setChanged] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  const hide = () => { setValue(null); setCopied(false); if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => { hide(); setReason(null); }, [userId, refreshKey]);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  if (!canManagePasswordOf(targetRole)) {
    return (
      <div style={{ fontSize: 12, color: '#94a3b8' }}>
        {canManagePasswords()
          ? '🔒 システム管理者のパスワードは、システム管理者か運営管理者だけが確認できます。'
          : '🔒 パスワードは陸上管理者・システム管理者だけが確認できます。'}
      </div>
    );
  }

  const reveal = async () => {
    if (value) { hide(); return; }
    setLoading(true); setReason(null);
    try {
      const r = await api.get<{ data: { password: string | null; reason?: string; password_changed_at?: string | null; password_changed_by?: string | null } }>(`/users/${userId}/password`);
      const d = r.data;
      setChanged(fmtChanged(d?.password_changed_at, d?.password_changed_by));
      if (d?.password) {
        setValue(d.password);
        timer.current = window.setTimeout(hide, AUTO_HIDE_MS);   // 出したままにしない
      } else {
        setReason(d?.reason ?? '表示できないパスワードです。');
      }
    } catch (e: any) {
      setReason(e?.message ?? 'パスワードの取得に失敗しました');
    } finally { setLoading(false); }
  };

  const copy = () => {
    if (!value) return;
    void navigator.clipboard?.writeText(value).then(() => setCopied(true)).catch(() => setCopied(false));
  };

  const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', background: '#fff' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {employeeCode && (
          <span style={{ fontSize: 12, color: '#64748b' }}>
            ログインID <b style={{ fontFamily: 'Consolas, monospace', color: '#0f172a', fontSize: 13 }}>{employeeCode}</b>
          </span>
        )}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 8, border: `1.5px solid ${value ? '#86efac' : '#e2e8f0'}`, background: value ? '#f0fdf4' : '#f8fafc', minWidth: 130 }}>
          <KeyRound size={13} color={value ? '#15803d' : '#94a3b8'} />
          <span style={{ fontFamily: 'Consolas, monospace', fontSize: value ? 15 : 13, fontWeight: 800, letterSpacing: '0.08em', color: value ? '#14532d' : '#94a3b8', userSelect: value ? 'all' : 'none' }}>
            {value ?? '••••••••'}
          </span>
        </span>
        <button type="button" onClick={() => void reveal()} disabled={loading}
          style={{ ...btn, border: '1px solid #bbf7d0', color: '#15803d' }}
          title="パスワードを表示します（60秒で自動的に伏せます。表示した記録は操作履歴に残ります）">
          {value ? <EyeOff size={13} /> : <Eye size={13} />}{loading ? '取得中…' : value ? '隠す' : '表示'}
        </button>
        {value && (
          <button type="button" onClick={copy} style={{ ...btn, border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
            <Copy size={13} />{copied ? 'コピーしました' : 'コピー'}
          </button>
        )}
      </div>
      {reason && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', lineHeight: 1.6 }}>
          {reason}{hint ? <><br />{hint}</> : null}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
        {changed ? `${changed}　` : ''}表示した記録は操作履歴に残ります。
      </div>
    </div>
  );
}
